import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  detectLanguage,
  getLanguageAlternate,
  routeLanguageMap,
  legalEsAwaitingTranslation,
  isNoindexRoute,
} from "@/utils/routes";

const BASE_URL = "https://luisroc.com";

export interface PageSeoContext {
  /** Idioma derivado de la URL, nunca de estado React. */
  language: "es" | "en";
  /** Ruta actual normalizada (sin barra final). */
  path: string;
  /** Canonical autorreferencial absoluto. */
  url: string;
  /** URL absoluta de la versión ES, o null si no existe equivalente. */
  esUrl: string | null;
  /** URL absoluta de la versión EN, o null si no existe equivalente. */
  enUrl: string | null;
  /** Cambia de idioma navegando a la URL equivalente. */
  onLanguageChange: (lang: "es" | "en") => void;
  /** true cuando la URL está en la lista noindex provisional del quality gate P3. */
  noindex: boolean;
}

/**
 * Fuente única de verdad de idioma + canonical + hreflang por página.
 *
 * Motivo (P3, QA ES/EN): muchas páginas derivaban el idioma de `useState('es')`
 * y pasaban una `url` fija a SEOWrapper. Eso provocaba que las URLs /en/*
 * sirvieran contenido en español y declararan canonical hacia la URL ES
 * (canonical entre idiomas = versión EN fuera del índice).
 */
export function usePageSeo(): PageSeoContext {
  const location = useLocation();
  const navigate = useNavigate();

  const raw = location.pathname;
  const path = raw.length > 1 ? raw.replace(/\/+$/, "") : raw;
  const language = detectLanguage(path);

  // Rutas legales ES que todavía sirven el documento en inglés: no son una
  // traducción real, así que se canonicalizan hacia el documento inglés y no
  // participan en hreflang (LEGAL_TRANSLATION_REQUIRED).
  const legalEnEquivalent = legalEsAwaitingTranslation[path];
  const noindex = isNoindexRoute(path);

  const mapped = legalEnEquivalent ? undefined : routeLanguageMap[path];
  const esPath = mapped ? mapped.es : getLanguageAlternate(path, "es");
  const enPath = mapped ? mapped.en : getLanguageAlternate(path, "en");

  // Solo se declara hreflang cuando la ruta equivalente está realmente mapeada.
  const hasPair = Boolean(mapped) || esPath !== enPath;
  const esUrl = hasPair ? `${BASE_URL}${esPath}` : language === "es" ? `${BASE_URL}${path}` : null;
  const enUrl = hasPair ? `${BASE_URL}${enPath}` : language === "en" ? `${BASE_URL}${path}` : null;

  const onLanguageChange = useCallback(
    (lang: "es" | "en") => {
      if (lang === language) return;
      const target = lang === "es" ? esPath : enPath;
      if (target && target !== path) navigate(target);
    },
    [language, esPath, enPath, path, navigate]
  );

  return {
    // El documento legal sigue escrito en inglés: se declara el idioma real.
    language: legalEnEquivalent ? "en" : language,
    path,
    url: legalEnEquivalent ? `${BASE_URL}${legalEnEquivalent}` : `${BASE_URL}${path}`,
    esUrl: legalEnEquivalent ? null : esUrl,
    enUrl: legalEnEquivalent ? null : enUrl,
    onLanguageChange,
    noindex,
  };
}
